import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, } from '@mui/material';
import { Lock, Globe, } from 'lucide-react';
import { useSelector } from 'react-redux';
import CommonDrawerLayout from '../../../common_components/CommonDrawerLayout';
import CommonTabs from '../../../common_components/CommonTabs';
import CommonLoader from '../../../common_components/CommonLoader';
import HoverTooltip from '../../../common_components/hoverOverlay/HoverTooltip';
import AppButton from '../../../common_components/AppButton';
import ConfirmActionModal from '../../../common_components/ConfirmActionModal';
import { FormField, TextArea } from '../../../common_components/FormInputs';
import { toast } from '../../../common_components/Toast/ToastUtils';
import { selectCanManageNotes, selectEntityType, selectIsSuperAdmin, selectToken, selectUser, } from '../../../redux/authSlice';
import reportBuilderService from '../../../services/reportBuilder.service';
/** Notes shown before "View More"; each tap on it reveals four more (as in Take Action). */
const INITIAL_VISIBLE = 3;
// Relative time like the Take Action history: "Just now", "5 min ago", "3 hours ago", then the date.
const timeFormatter = (dateStr) => {
    if (!dateStr)
        return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime()))
        return '';
    const diffInSec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diffInSec < 60)
        return 'Just now';
    const diffInMin = Math.floor(diffInSec / 60);
    if (diffInMin < 60)
        return `${diffInMin} min ago`;
    const diffInHours = Math.floor(diffInMin / 60);
    if (diffInHours < 24)
        return `${diffInHours} hours ago`;
    return `${d.toLocaleString('en-US', { month: 'long', day: 'numeric' })} ${d.getFullYear()}, ${d
        .toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true })
        .toLowerCase()}`;
};
// Generate initials for avatar
const getInitials = (name) => {
    if (!name)
        return 'U';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1)
        return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};
const ReportNotesDrawer = ({ open, onClose, reportId, reportName, canManage, onCountChange, }) => {
    const token = useSelector(selectToken);
    const currentUser = useSelector(selectUser);
    const entityType = useSelector(selectEntityType);
    const isSuperAdmin = useSelector(selectIsSuperAdmin);
    const hasManageNotes = useSelector(selectCanManageNotes);
    const isClient = entityType === 'client';
    // Internal users add notes only with "Manage Notes"; clients are not gated by it.
    const effectiveCanManage = canManage !== undefined ? canManage : (!isClient && (isSuperAdmin || hasManageNotes));
    // Active tab: 'internal' or 'external'. Clients only see 'external'.
    const [activeTab, setActiveTab] = useState('internal');
    useEffect(() => {
        if (isClient) {
            setActiveTab('external');
        }
        else {
            setActiveTab('internal');
        }
    }, [isClient, open]);
    // Notes state
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(false);
    /** How many history entries are showing - "View More" adds four. */
    const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
    // New note input state
    const [newNoteText, setNewNoteText] = useState('');
    const [submitting, setSubmitting] = useState(false);
    // Editing note state
    const [editingNoteId, setEditingNoteId] = useState(null);
    const [editNoteText, setEditNoteText] = useState('');
    const [savingEdit, setSavingEdit] = useState(false);
    // Deleting state
    const [deleteTargetId, setDeleteTargetId] = useState(null);
    const [deleting, setDeleting] = useState(false);
    // Load notes from API
    const fetchNotes = useCallback(async () => {
        if (!reportId || !token)
            return;
        setLoading(true);
        try {
            const res = await reportBuilderService.listReportNotes(reportId, token);
            if (res?.data?.success) {
                const loaded = res.data.data?.notes || [];
                setNotes(loaded);
                onCountChange?.(loaded.length);
            }
            else {
                setNotes([]);
                onCountChange?.(0);
            }
        }
        catch (err) {
            setNotes([]);
            toast.error(err?.message || 'Failed to load report notes.');
        }
        finally {
            setLoading(false);
        }
    }, [reportId, token, onCountChange]);
    useEffect(() => {
        if (open && reportId) {
            fetchNotes();
            setVisibleCount(INITIAL_VISIBLE);
            setNewNoteText('');
            setEditingNoteId(null);
            setEditNoteText('');
        }
    }, [open, reportId, fetchNotes]);
    // Counts for tabs
    const internalCount = useMemo(() => notes.filter((n) => n.type === 'internal').length, [notes]);
    const externalCount = useMemo(() => notes.filter((n) => n.type === 'external').length, [notes]);
    // Filter notes by active tab
    const currentTabNotes = useMemo(() => notes.filter((n) => n.type === activeTab), [notes, activeTab]);
    // Add note handler
    const handleAddNote = async (e) => {
        if (e)
            e.preventDefault();
        const trimmed = newNoteText.trim();
        if (!trimmed) {
            toast.error('Please enter a note before sending.');
            return;
        }
        if (!reportId || !token)
            return;
        setSubmitting(true);
        try {
            const res = await reportBuilderService.addReportNote(reportId, { type: activeTab, note: trimmed }, token);
            if (res?.data?.success) {
                toast.success('Note added successfully.');
                setNewNoteText('');
                await fetchNotes();
            }
            else {
                toast.error(res?.data?.message || 'Failed to add note.');
            }
        }
        catch (err) {
            toast.error(err?.message || 'Could not add note. Please try again.');
        }
        finally {
            setSubmitting(false);
        }
    };
    // Edit start
    const handleStartEdit = (note) => {
        setEditingNoteId(note._id);
        setEditNoteText(note.note);
    };
    // Edit cancel
    const handleCancelEdit = () => {
        setEditingNoteId(null);
        setEditNoteText('');
    };
    // Edit save
    const handleSaveEdit = async (noteId) => {
        const trimmed = editNoteText.trim();
        if (!trimmed) {
            toast.error('Note cannot be empty.');
            return;
        }
        setSavingEdit(true);
        try {
            const res = await reportBuilderService.updateReportNote(reportId, noteId, { note: trimmed }, token);
            if (res?.data?.success) {
                toast.success('Note updated successfully.');
                setEditingNoteId(null);
                setEditNoteText('');
                await fetchNotes();
            }
            else {
                toast.error(res?.data?.message || 'Failed to update note.');
            }
        }
        catch (err) {
            toast.error(err?.message || 'Could not update note.');
        }
        finally {
            setSavingEdit(false);
        }
    };
    // Delete note handler
    const handleDeleteConfirm = async () => {
        if (!deleteTargetId)
            return;
        setDeleting(true);
        try {
            const res = await reportBuilderService.deleteReportNote(reportId, deleteTargetId, token);
            if (res?.data?.success) {
                toast.success('Note deleted successfully.');
                setDeleteTargetId(null);
                await fetchNotes();
            }
            else {
                toast.error(res?.data?.message || 'Failed to delete note.');
            }
        }
        catch (err) {
            toast.error(err?.message || 'Could not delete note.');
        }
        finally {
            setDeleting(false);
        }
    };
    // Check if user can modify a specific note:
    // 1. Super Admin can manage all notes
    // 2. Client contacts can edit/delete their own comments
    // 3. Internal users: only if they have "Manage Notes" permission can they edit/delete their own notes
    const canModifyNote = (note) => {
        if (isSuperAdmin)
            return true;
        const currentUserId = String(currentUser?._id || currentUser?.user_id || '');
        const isAuthor = Boolean(note.user_id && String(note.user_id) === currentUserId);
        if (isClient) {
            return isAuthor;
        }
        if (!effectiveCanManage)
            return false;
        return isAuthor;
    };
    return (_jsxs(_Fragment, { children: [_jsx(CommonDrawerLayout, { open: open, onClose: onClose, title: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: ["Report Notes", reportName && (_jsxs(_Fragment, { children: [_jsx(Typography, { sx: { color: '#9CA3AF', fontWeight: 400 }, children: "|" }), _jsx(HoverTooltip, { title: reportName, children: _jsx(Typography, { sx: {
                                            maxWidth: 300,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            fontWeight: 600,
                                            color: '#111827',
                                            fontSize: '16px',
                                        }, children: reportName }) })] }))] }), width: "md", showOverlay: true, closeOnOverlayClick: true, children: _jsx(Box, { sx: { p: 1 }, children: _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1, position: 'relative' }, children: [!isClient && (_jsx(CommonTabs, { fullWidth: true, selectedValue: activeTab, onChange: (value) => {
                                    setActiveTab(value);
                                    setEditingNoteId(null);
                                    setVisibleCount(INITIAL_VISIBLE);
                                }, tabs: [
                                    {
                                        value: 'internal',
                                        label: (_jsxs(Box, { sx: { display: 'inline-flex', alignItems: 'center', gap: 1 }, children: [_jsx(Lock, { size: 14 }), "Internal Notes (", internalCount, ")"] })),
                                    },
                                    {
                                        value: 'external',
                                        label: (_jsxs(Box, { sx: { display: 'inline-flex', alignItems: 'center', gap: 1 }, children: [_jsx(Globe, { size: 14 }), "External Notes (", externalCount, ")"] })),
                                    },
                                ] })), (isClient || effectiveCanManage) && (_jsxs(Box, { sx: {
                                    border: '1px solid #E5E7EB',
                                    backgroundColor: '#ffff',
                                    borderRadius: 1,
                                    padding: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                }, children: [_jsx("h3", { className: "all-Font-Sizes ", style: { color: '#FF008E', margin: 0, borderBottom: '1px solid #EAECF0', paddingBottom: 8 }, children: activeTab === 'internal' ? 'Add Internal Note' : 'Add External Note' }), _jsxs(Box, { component: "form", onSubmit: handleAddNote, children: [_jsx(FormField, { children: _jsx(TextArea, { label: "Comment", minRows: 4, value: newNoteText, onChange: (e) => setNewNoteText(e.target.value), placeholder: activeTab === 'internal'
                                                        ? 'Leave internal comments or instructions here...'
                                                        : 'Leave external comments for the client here...', disabled: submitting }) }), _jsx("div", { style: { display: 'flex', justifyContent: 'end' }, children: _jsx(AppButton, { variant: "contained", type: "submit", loading: submitting, disabled: submitting || !newNoteText.trim(), sx: {
                                                        mt: 2,
                                                        background: '#FF008E',
                                                        borderColor: '#FF008E',
                                                        '&:hover': { background: '#db2777' },
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                    }, children: "Save Note" }) })] })] })), _jsxs(Box, { sx: {
                                    border: '1px solid #E5E7EB',
                                    backgroundColor: '#FFFFFF',
                                    borderRadius: '8px',
                                    padding: '8px',
                                }, children: [_jsx(Typography, { className: "all-Font-Sizes ", sx: { color: '#FF008E', fontWeight: 600, pb: 1, borderBottom: '1px solid #EAECF0', mb: '10px' }, children: "History" }), loading && (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', py: 3 }, children: _jsx(CommonLoader, { className: "table-loader" }) })), !loading && currentTabNotes.length === 0 && (_jsxs(Typography, { fontSize: "14px", color: "#6B7280", children: ["No ", activeTab, " notes yet. Notes added for this report will appear here."] })), _jsxs(Box, { sx: {
                                            maxHeight: '500px',
                                            overflow: 'hidden',
                                            overflowY: 'auto',
                                            '&::-webkit-scrollbar': { display: 'none' },
                                            msOverflowStyle: 'none',
                                            scrollbarWidth: 'none',
                                        }, children: [!loading &&
                                                currentTabNotes.slice(0, visibleCount).map((note, index) => {
                                                    const isEditing = editingNoteId === note._id;
                                                    const allowModify = canModifyNote(note);
                                                    const isLastVisible = index === Math.min(visibleCount, currentTabNotes.length) - 1;
                                                    const hasMore = currentTabNotes.length > visibleCount;
                                                    return (_jsxs(Box, { sx: {
                                                            display: 'flex',
                                                            gap: '8px',
                                                            position: 'relative',
                                                            mb: isLastVisible && !hasMore ? 0 : '8px',
                                                        }, children: [(!isLastVisible || hasMore) && (_jsx(Box, { sx: {
                                                                    position: 'absolute',
                                                                    top: '36px',
                                                                    bottom: '-32px',
                                                                    left: '17px',
                                                                    width: '1px',
                                                                    backgroundColor: '#E5E7EB',
                                                                    zIndex: 0,
                                                                } })), _jsx(Box, { sx: {
                                                                    width: '36px',
                                                                    height: '36px',
                                                                    borderRadius: '8px',
                                                                    border: '1px solid #E5E7EB',
                                                                    background: '#FFFFFF',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    fontSize: '14px',
                                                                    fontWeight: 600,
                                                                    color: '#3B82F6',
                                                                    zIndex: 1,
                                                                    flexShrink: 0,
                                                                }, children: getInitials(note.user_name) }), _jsxs(Box, { sx: { width: '100%', mt: '-2px' }, children: [_jsx(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }, children: _jsxs(Typography, { className: "all-Font-Sizes", sx: { fontWeight: 600, color: '#111827', display: 'flex', gap: 1, alignItems: 'center' }, children: [note.user_name || 'Anonymous', (note.designation || note.role) && (_jsxs("span", { style: { fontWeight: 400, color: '#6B7280', fontSize: '14px' }, children: ["(", note.designation || note.role, ")"] }))] }) }), _jsxs(Typography, { sx: { fontSize: '12px', color: '#6B7280', mt: '2px', mb: '8px' }, children: [timeFormatter(note.created_at), note.updated_at && note.updated_at !== note.created_at && (_jsx(Box, { component: "span", sx: { ml: 0.75, fontStyle: 'italic' }, children: "(edited)" }))] }), isEditing ? (_jsxs(Box, { sx: { mt: 0.5, display: 'flex', flexDirection: 'column', gap: 1 }, children: [_jsx(TextArea, { value: editNoteText, onChange: (e) => setEditNoteText(e.target.value), disabled: savingEdit, minRows: 2 }), _jsxs(Box, { sx: { display: 'flex', justifyContent: 'flex-end', gap: 1 }, children: [_jsx(AppButton, { variant: "outlined", onClick: handleCancelEdit, disabled: savingEdit, sx: { textTransform: 'none', color: '#475467', borderColor: '#D0D5DD' }, children: "Cancel" }), _jsx(AppButton, { variant: "contained", onClick: () => handleSaveEdit(note._id), loading: savingEdit, disabled: savingEdit || !editNoteText.trim(), sx: { textTransform: 'none', background: '#FF008E', '&:hover': { background: '#db2777' } }, children: "Save" })] })] })) : (_jsx(Typography, { sx: {
                                                                            fontSize: '13px',
                                                                            color: '#5a6370',
                                                                            lineHeight: 1.5,
                                                                            mb: '8px',
                                                                            whiteSpace: 'pre-wrap',
                                                                            wordBreak: 'break-word',
                                                                        }, children: note.note }))] })] }, note._id));
                                                }), !loading && currentTabNotes.length > visibleCount && (_jsxs(Box, { sx: { display: 'flex', position: 'relative', mt: '22px' }, children: [_jsx(Box, { sx: {
                                                            position: 'absolute',
                                                            top: '-32px',
                                                            left: '17px',
                                                            width: '22px',
                                                            height: '48px',
                                                            borderBottom: '1px solid #E5E7EB',
                                                            borderLeft: '1px solid #E5E7EB',
                                                            borderBottomLeftRadius: '8px',
                                                            zIndex: 0,
                                                        } }), _jsxs(AppButton, { variant: "outlined", onClick: () => setVisibleCount((prev) => prev + 4), sx: {
                                                            ml: '39px',
                                                            textTransform: 'none',
                                                            borderColor: '#E5E7EB',
                                                            color: '#111827',
                                                            fontWeight: 600,
                                                            borderRadius: '8px',
                                                            py: '4px',
                                                            px: '12px',
                                                            fontSize: '14px',
                                                            backgroundColor: '#FFFFFF',
                                                            '&:hover': { borderColor: '#D1D5DB', backgroundColor: '#F9FAFB' },
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                        }, children: [_jsx("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: _jsx("path", { d: "M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.3" }) }), "View More (", currentTabNotes.length - visibleCount, ")"] })] }))] })] })] }) }) }), _jsx(ConfirmActionModal, { open: Boolean(deleteTargetId), title: "Delete Note", description: "Are you sure you want to delete this note? This action cannot be undone.", confirmText: "Delete", cancelText: "Cancel", isDeleteIcon: true, loading: deleting, onClose: () => {
                    if (!deleting)
                        setDeleteTargetId(null);
                }, onConfirm: handleDeleteConfirm })] }));
};
export default ReportNotesDrawer;
